class ArticleEngagement {

    constructor(options) {
        const defaults = {
            articleId: '',
            contentSelector: '.main-text',
            apiBaseUrl: '',
            eventsPath: '/events',
            placement: 'article_body',
            sessionStorageKey: 'onsDiscoverSessionId',
            anonymousStorageKey: 'onsDiscoverAnonymousId',
            completionReadSeconds: 30,
            completionReadProgress: 0.25,
            completionMaxReadSeconds: 90,
            readTimeIntervalSeconds: 5,
            minimumVisibleRatio: 0.25
        };
        this.params = Object.assign({}, defaults, options || {});
        this.content = document.querySelector(this.params.contentSelector);
        this.lastParagraph = this.getLastParagraph();
        this.articleId = this.resolveArticleId();
        this.readingTime = this.getReadingTime();
        this.sessionId = this.getOrCreateId(this.params.sessionStorageKey, window.sessionStorage);
        this.anonymousId = this.getOrCreateId(this.params.anonymousStorageKey, window.localStorage);
        this.completed = false;
        this.visibleSeconds = 0;
        this.lastTickAt = null;
        this.readTimer = null;
        this.start();
    }

    start() {
        if (!this.articleId || !this.content) {
            return;
        }

        this.trackEvent('article_view', {
            page_url: window.location.href,
            referrer: document.referrer || null
        });

        this.onScroll = this.handleScroll.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.startReadTimer();
        this.handleScroll();
    }

    resolveArticleId() {
        if (this.params.articleId) {
            return String(this.params.articleId);
        }

        const meta = document.querySelector('meta[name="newsbomb:article-id"]');
        return meta ? meta.getAttribute('content') : '';
    }

    getReadingTime() {
        if (!window.Joomla || typeof window.Joomla.getOptions !== 'function') {
            return 1;
        }

        const pageInfo = window.Joomla.getOptions('page-info', {});
        const readingTime = Number(pageInfo.length);

        if (!Number.isFinite(readingTime) || readingTime <= 0) {
            return 1;
        }

        return readingTime;
    }

    getOrCreateId(key, storage) {
        try {
            const existing = storage.getItem(key);
            if (existing) {
                return existing;
            }

            const id = this.createId();
            storage.setItem(key, id);
            return id;
        } catch (error) {
            return this.createId();
        }
    }

    createId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }

        return 'rec_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
    }

    endpoint(path) {
        return String(this.params.apiBaseUrl).replace(/\/$/, '') + path;
    }

    handleScroll() {
        const depth = this.contentScrollDepth();
        this.maybeTrackCompletion(depth);
    }

    contentScrollDepth() {
        const rect = this.content.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const contentHeight = Math.max(this.content.offsetHeight, 1);
        const viewed = Math.min(Math.max(viewportHeight - rect.top, 0), contentHeight);
        return Math.round((viewed / contentHeight) * 100);
    }

    startReadTimer() {
        this.lastTickAt = Date.now();
        this.readTimer = window.setInterval(() => {
            const now = Date.now();
            const elapsedSeconds = Math.max(0, Math.round((now - this.lastTickAt) / 1000));
            this.lastTickAt = now;

            if (document.hidden || !this.isContentVisible()) {
                return;
            }

            this.visibleSeconds += elapsedSeconds;
            this.maybeTrackCompletion(this.contentScrollDepth());
        }, this.params.readTimeIntervalSeconds * 1000);
    }

    handleVisibilityChange() {
        this.lastTickAt = Date.now();
    }

    isContentVisible() {
        const rect = this.content.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
        const ratio = Math.max(0, visibleHeight) / Math.max(1, Math.min(rect.height, viewportHeight));
        return ratio >= this.params.minimumVisibleRatio;
    }

    maybeTrackCompletion(depth) {
        if (this.completed) {
            return;
        }

        if (
            this.hasReachedLastParagraph()
            && this.visibleSeconds >= this.completionReadSeconds()
        ) {
            this.completed = true;
            this.trackEvent('article_completed', this.readingMetadata({
                depth: depth,
                seconds: this.visibleSeconds,
                completion_required_seconds: this.completionReadSeconds(),
                article_end_reached: true,
                content_selector: this.params.contentSelector
            }));
        }
    }

    completionReadSeconds() {
        const readingTimeSeconds = this.readingTime * 60;
        const progressSeconds = Math.ceil(
            readingTimeSeconds * this.params.completionReadProgress
        );
        const cappedSeconds = Math.min(
            progressSeconds,
            this.params.completionMaxReadSeconds
        );

        return Math.max(this.params.completionReadSeconds, cappedSeconds);
    }

    getLastParagraph() {
        const paragraphs = this.content ? this.content.querySelectorAll('p') : [];

        if (!paragraphs.length) {
            return null;
        }

        return paragraphs[paragraphs.length - 1];
    }

    hasReachedLastParagraph() {
        if (!this.lastParagraph) {
            return false;
        }

        const rect = this.lastParagraph.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        return rect.bottom <= viewportHeight;
    }

    readingMetadata(metadata) {
        const readingTimeSeconds = this.readingTime * 60;
        const seconds = Number(metadata.seconds);
        const trackedSeconds = Number.isFinite(seconds) ? seconds : this.visibleSeconds;
        const readingProgress = Math.min(
            trackedSeconds / readingTimeSeconds,
            1
        );

        metadata.estimated_read_minutes = this.readingTime;
        metadata.reading_progress = readingProgress;

        return metadata;
    }

    async trackEvent(eventType, metadata) {
        const payload = {
            event_type: eventType,
            article_id: this.articleId,
            anonymous_id: this.anonymousId,
            session_id: this.sessionId,
            placement: this.params.placement,
            metadata: metadata || {}
        };
        const url = this.endpoint(this.params.eventsPath);

        if (window.fetch) {
            try {
                await window.fetch(url, {
                    method: 'POST',
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                return;
            } catch (error) {
                console.log(error);
            }
        }
    }
}

export {
    ArticleEngagement as
        default
};
